import React, { useContext, useState, useEffect } from 'react';
import { useFormikContext } from 'formik';
import { isEmpty } from 'lodash';
import { ArnContext, FormContext } from 'contexts';
import { ArnApplicationDvsMethodFormData } from 'types/forms/arnApplicationTypes';
import { IFormAlertType } from 'types/forms/formTypes';
import ChangeOfNameCertificateDetails from './ChangeOfNameCertificateDetails';
import MarriageCertificateDetails from './MarriageCertificateDetails';
import FileUpload from './FileUpload';
import {
  ApplicationName,
  FormNavigation,
} from 'components/CASAForm';
import { AuDirectionLink } from 'components/DTA';
import { Loader } from 'components/common';
import { NAME_CHANGE_DOC_TYPES } from 'arnConstants';
import { getNameChangeDocumentTitleAndDataModel } from 'helpers/arnApplicationHelper';
import { useArnService } from 'services';
import { IDvsCheckRequest } from 'services/arnService/arnServiceTypes';
import { FORM_VALIDATION_ERROR_ALERT } from 'arnConstants';

interface NameChangeDetailsProps {
  nextStep: string;
}

const NameChangeDocumentSelection: React.FC<NameChangeDetailsProps> = (props: NameChangeDetailsProps) => {
  const { setPreviousStep, previousStep } = useContext(ArnContext);
  const { values, validateForm, setFieldValue } = useFormikContext<ArnApplicationDvsMethodFormData>();
  const { setFormAlert, openStepById, setOnChangeValidation, handleBack } = useContext(FormContext);
  const arnService = useArnService();
  const [checkDvs, setCheckDvs] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDvsCheckSuccessful, setIsDvsCheckSuccessful] = useState<boolean>(false);

  useEffect(() => {
    async function devCheck () {
      if (checkDvs) {
        setIsLoading(true);
        const dvsCheckData = getDvsCheckRequestData();
        setCheckDvs(false);
        try {
          const dvsCkeckResult = await arnService.getDvsCheckResult(dvsCheckData);
          setIsDvsCheckSuccessful(dvsCkeckResult.dvsResponse);
          setIsLoading(false);
          let alert: IFormAlertType = {
            as: 'success',
            title: 'Your identification has been successfully verified'
          };
          setDvsIndicatorValue();

          if (dvsCkeckResult.dvsResponse) {

            setFormAlert(null);
            openStepById(props.nextStep);
            setPreviousStep('NameChangeDetails');
            return;
          }

          if (dvsCkeckResult.errors) {
            setFormAlert(FORM_VALIDATION_ERROR_ALERT);
            return;
          }

          alert = {
            as: 'error',
            title: 'There is a problem with our verification service',
            message: <>Please try again later. Alternatively you can <a href='' onClick={handleManualUpload}>upload a certified copy of your documentation </a> for manual verification.</>
          };
          setFormAlert(alert);
          return;
        }
        catch (error) {
          setFormAlert(FORM_VALIDATION_ERROR_ALERT);
          setIsLoading(false);
        }
      }
    }
    devCheck();
  }, [checkDvs]);

  const handleManualUpload = (event: React.MouseEvent<HTMLElement>) => {
    const identityType = getNameChangeDocumentTitleAndDataModel(values.previousName?.changeOfNameDocument);
    event.preventDefault();
    const fileUploadAlert: IFormAlertType = {
      as: 'warning',
      title: 'There was a problem verifying your document.',
      message: <>Your identification ({identityType?.title}) can&apos;t be verified.</>
    };

    setFieldValue(`${identityType.identityType}.isManualUpload`, true);
    setFormAlert(fileUploadAlert);
    const previousStepId = 'UploadFileForManualNameChangeIdentification';
    setPreviousStep(previousStepId);
    openStepById(previousStepId);
  };

  const setDvsIndicatorValue = () => {
    const dvsCheckResult = isDvsCheckSuccessful ? 'Passed' : 'Failed';
    if (values?.previousName?.changeOfNameDocument === NAME_CHANGE_DOC_TYPES.NAME_CHANGE_CERTIFICATE) {
      setFieldValue('changeOfNameCertificateDetails.dvsValidationIndicator', dvsCheckResult);
    }
    if (values?.previousName?.changeOfNameDocument === NAME_CHANGE_DOC_TYPES.MARRIAGE_CERTIFICATE) {
      setFieldValue('marriageCertificateDetails.dvsValidationIndicator', dvsCheckResult);
    }
  };

  const getDvsCheckRequestData = () => {
    const dvsCheckRequestModel: IDvsCheckRequest = {
      identityType: '',
      familyName: '',
      givenNames: '',
      dateOfBirth: null,
      passportNumber: null,
      registrationNumber: null,
      registrationDate: null,
      acquisitionDate: null,
      certificateNumber: null,
      immiCardNumber: null,
      previousFamilyName: null,
      previousGivenNames: null,
      registrationState: null,
      registrationYear: null,
      certificateIssueDate: null,
      secondPersonFamilyName: null,
      secondPersonGivenNames: null,
      dateOfEvent: null
    };

    if (values?.previousName?.changeOfNameDocument === NAME_CHANGE_DOC_TYPES.NAME_CHANGE_CERTIFICATE) {
      dvsCheckRequestModel.identityType = NAME_CHANGE_DOC_TYPES.NAME_CHANGE_CERTIFICATE;
      dvsCheckRequestModel.familyName = values.changeOfNameCertificateDetails.familyName;
      dvsCheckRequestModel.givenNames = values.changeOfNameCertificateDetails.givenNames;
      dvsCheckRequestModel.dateOfBirth = values.changeOfNameCertificateDetails.dateOfBirth;
      dvsCheckRequestModel.registrationNumber = values.changeOfNameCertificateDetails.registrationNumber;
      dvsCheckRequestModel.registrationState = values.changeOfNameCertificateDetails.registrationState;
      dvsCheckRequestModel.certificateNumber = values.changeOfNameCertificateDetails.certificateNumber;

      return dvsCheckRequestModel;
    }
    if (values?.previousName?.changeOfNameDocument === NAME_CHANGE_DOC_TYPES.MARRIAGE_CERTIFICATE) {

      dvsCheckRequestModel.identityType = NAME_CHANGE_DOC_TYPES.MARRIAGE_CERTIFICATE;
      dvsCheckRequestModel.familyName = values.marriageCertificateDetails.firstPersonFamilyName;
      dvsCheckRequestModel.givenNames = values.marriageCertificateDetails.firstPersonGivenNames;
      dvsCheckRequestModel.secondPersonFamilyName = values.marriageCertificateDetails.secondPersonFamilyName;
      dvsCheckRequestModel.secondPersonGivenNames = values.marriageCertificateDetails.secondPersonGivenNames;
      dvsCheckRequestModel.dateOfEvent = values.marriageCertificateDetails.dateOfMarriage;
      dvsCheckRequestModel.registrationState = values.marriageCertificateDetails.registrationState;
      dvsCheckRequestModel.registrationNumber = values.marriageCertificateDetails.registrationNumber;
      dvsCheckRequestModel.certificateNumber = values.marriageCertificateDetails.certificateNumber;

      return dvsCheckRequestModel;
    }
  };
  const handleNext = async () => {
    const validationErrors = await validateForm();

    if (!validationErrors || isEmpty(validationErrors)) {
      setOnChangeValidation(false);

      if (values?.previousName?.changeOfNameDocument != NAME_CHANGE_DOC_TYPES.DIVORCE_PAPERS
        || values?.previousName?.changeOfNameDocument === NAME_CHANGE_DOC_TYPES.NONE) {
        setCheckDvs(true);
      }
      else {
        openStepById(props.nextStep);
        setPreviousStep('NameChangeDetails');
        return;
      }
    }
    else {
      setOnChangeValidation(true);
    }
  };

  const handleBackAction = () => {
    if (previousStep && previousStep === 'NameChangeDetails') {
      setPreviousStep('PreviousName');
      openStepById('PreviousName');
      return;
    }

    if (previousStep) {
      openStepById(previousStep);
      return;
    }

    handleBack();
  };

  const getNameChangeDetailsPage = () => {
    if (values?.previousName?.changeOfNameDocument === NAME_CHANGE_DOC_TYPES.NAME_CHANGE_CERTIFICATE) {
      return <ChangeOfNameCertificateDetails />;
    }
    if (values?.previousName?.changeOfNameDocument === NAME_CHANGE_DOC_TYPES.MARRIAGE_CERTIFICATE) {
      return <MarriageCertificateDetails />;
    }
    if (values?.previousName?.changeOfNameDocument === NAME_CHANGE_DOC_TYPES.DIVORCE_PAPERS) {
      return <FileUpload documentType='Australian divorce certificate' identityType='divorcePapersDetails' />;
    }
  };

  const getPageTitle = () => {
    if (values?.previousName?.changeOfNameDocument === NAME_CHANGE_DOC_TYPES.NAME_CHANGE_CERTIFICATE) {
      return 'Change of name certificate details';
    }
    if (values?.previousName?.changeOfNameDocument === NAME_CHANGE_DOC_TYPES.MARRIAGE_CERTIFICATE) {
      return 'Australian marriage certificate details';
    }
    if (values?.previousName?.changeOfNameDocument === NAME_CHANGE_DOC_TYPES.DIVORCE_PAPERS) {
      return 'Upload identification';
    }
    return '';
  };

  const detailsPage = getNameChangeDetailsPage();
  const pageTitle = getPageTitle();

  if (isLoading) {
    return <Loader message='Working. Please wait.....' />;
  }
  return (
    <>
      <ApplicationName name={pageTitle} />
      <AuDirectionLink
        onClick={handleBackAction}
        text="Back"
        direction="left" />
      {detailsPage}
      <FormNavigation
        nextButtonText='Next'
        onNextAction={handleNext}
        hidePrevious
        showSaveAndExit={false} />
    </>
  );
};

export default NameChangeDocumentSelection;